import React, { useState, useEffect } from 'react';

// --- Helper Components ---

// Icon for the settings button and close button
const CogIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mr-2"
  >
    <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
    <path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    <path d="M12 2v2" />
    <path d="M12 22v-2" />
    <path d="m17 20.66-1-1.73" />
    <path d="M11 10.27 7 3.34" />
    <path d="m20.66 17-1.73-1" />
    <path d="M3.34 7 4.34 8" />
    <path d="M14 12h8" />
    <path d="M2 12h2" />
    <path d="m20.66 7-1.73 1" />
    <path d="M3.34 17 4.34 16" />
    <path d="m17 3.34-1 1.73" />
    <path d="M11 13.73 7 20.66" />
  </svg>
);

const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// --- Main Cookie Consent Component ---

const CookieConsent = () => {
  // State to manage the visibility of the banner and the settings modal
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // State for cookie preferences. Defaults all non-essential cookies to false.
  const [preferences, setPreferences] = useState({
    necessary: true, // Strictly necessary cookies are always enabled
    analytics: false,
    marketing: false,
  });

  // Check for existing consent in local storage when the component mounts
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    // If no consent has been given, show the banner.
    if (!consent) {
      setShowBanner(true);
    } else {
      // If consent was given, parse the preferences and update the state.
      // In a real app, you would now use these preferences to initialize tracking scripts.
      try {
        const parsedConsent = JSON.parse(consent);
        setPreferences(parsedConsent.preferences);
      } catch (e) {
        console.error('Could not parse cookie consent from local storage:', e);
        setShowBanner(true); // Show banner if stored data is corrupt
      }
    }
  }, []);

  /**
   * Saves the user's consent choice to local storage.
   * @param {object} consent - The consent object to save.
   */
  const saveConsent = (consent) => {
    localStorage.setItem('cookie_consent', JSON.stringify(consent));
    // Here you would initialize analytics, marketing scripts, etc. based on consent.
    console.log('Cookie consent saved:', consent);
    if (consent.preferences.analytics) {
      console.log(
        'Analytics cookies have been accepted. Initializing analytics scripts...'
      );
      // Example: window.initializeGoogleAnalytics();
    }
    if (consent.preferences.marketing) {
      console.log(
        'Marketing cookies have been accepted. Initializing marketing scripts...'
      );
      // Example: window.initializeFacebookPixel();
    }
  };

  // Handler for the "Accept All" button
  const handleAcceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(newPreferences);
    saveConsent({ accepted: true, preferences: newPreferences });
    setShowBanner(false);
    setShowSettings(false);
  };

  // Handler for the "Reject All" button
  const handleRejectAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(newPreferences);
    saveConsent({ accepted: false, preferences: newPreferences });
    setShowBanner(false);
    setShowSettings(false);
  };

  // Handler for saving custom preferences from the settings modal
  const handleSavePreferences = () => {
    saveConsent({ accepted: true, preferences });
    setShowBanner(false);
    setShowSettings(false);
  };

  // Toggles a specific cookie preference in the settings modal
  const togglePreference = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Don't render anything if the banner shouldn't be shown
  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Main Cookie Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 sm:p-6 shadow-2xl z-50 transform transition-transform duration-300 ${showBanner ? 'translate-y-0' : 'translate-y-full'} max-w-full sm:max-w-screen-xl mx-auto`}
        style={{ maxHeight: '40vh', minHeight: 'auto', overflowY: 'auto' }}
      >
        <div className="px-2 sm:px-4">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold leading-7 sm:truncate">
                We Value Your Privacy
              </h2>
              <p className="mt-2 text-gray-300 max-w-3xl text-xs sm:text-base">
                We use cookies and other tracking technologies to improve your
                browsing experience on our website, to show you personalized
                content, to analyze our website traffic, and to understand where
                our visitors are coming from. By clicking "Accept All", you
                consent to our use of cookies and other tracking technologies.
              </p>
            </div>
            <div className="mt-3 flex md:mt-0 md:ml-4 space-x-2 sm:space-x-3">
              <button
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center px-2 py-1 sm:px-4 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-gray-900 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <CogIcon />
                <span className="hidden sm:inline">Customize</span>
              </button>
              <button
                onClick={handleAcceptAll}
                className="inline-flex items-center px-2 py-1 sm:px-4 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[99999] flex items-center justify-center p-2 sm:p-4">
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-2xl transform transition-all overflow-y-auto"
            style={{ maxHeight: '90vh' }}
          >
            <div className="p-4 sm:p-8">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900">
                    Cookie Preferences
                  </h3>
                  <p className="mt-2 text-gray-600 text-xs sm:text-base">
                    Manage your cookie settings. You can enable or disable
                    different types of cookies below.
                  </p>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon />
                </button>
              </div>

              <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
                {/* Strictly Necessary Cookies */}
                <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-100 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-800 text-xs sm:text-base">
                      Strictly Necessary
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600">
                      These cookies are essential for the website to function
                      and cannot be switched off.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-not-allowed">
                    <input
                      type="checkbox"
                      checked
                      readOnly
                      className="sr-only peer"
                    />
                    <div className="w-8 h-4 sm:w-11 sm:h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 sm:after:h-5 after:w-3 sm:after:w-5 after:transition-all peer-checked:bg-green-600 opacity-50"></div>
                  </label>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-2 sm:p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-800 text-xs sm:text-base">
                      Analytics Cookies
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600">
                      These cookies allow us to count visits and traffic sources
                      to measure and improve our site's performance.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                      className="sr-only peer"
                    />
                    <div className="w-8 h-4 sm:w-11 sm:h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 sm:after:h-5 after:w-3 sm:after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-2 sm:p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-800 text-xs sm:text-base">
                      Marketing Cookies
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600">
                      These cookies may be set through our site by our
                      advertising partners to build a profile of your interests.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => togglePreference('marketing')}
                      className="sr-only peer"
                    />
                    <div className="w-8 h-4 sm:w-11 sm:h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 sm:after:h-5 after:w-3 sm:after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 sm:px-8 py-2 sm:py-4 flex justify-end space-x-2 sm:space-x-4 rounded-b-lg">
              <button
                onClick={handleRejectAll}
                className="px-3 py-1 sm:px-6 sm:py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Reject All
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-3 py-1 sm:px-6 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default CookieConsent;
